import { type RootState } from "../../redux/store";
import { selectProductDescriptor } from "../../redux/products/productsSelectors";
import { useSelector } from "react-redux";
import { Card } from "../../Configurators/Linear/components/Card";
import { PdpAccordionType } from "../redux/productDetailsTypes";
import { Collapse } from "~/common/components/Collapse";
import { userIsConsumerDistributor, userIsCustomer, userIsDistributor } from "../../utils/roles";
import { SearchResultProductCustomer } from "../../Search/components/SearchResults/ListView/SearchResultProductCustomer";
import { SearchResultProductNonCustomer } from "../../Search/components/SearchResults/ListView/SearchResultProductNonCustomer";
import { useBooleanState } from "../../common/hooks/useBooleanState";
import { ToggleIcon } from "../../common/components/icon/ToggleIcon";
import classNames from "classnames";
import { type SearchResultRow } from "../../utils/models";
import { useEffect, useMemo } from "react";
import {
  trackUserClickRecommendedProductSectionDetails,
  trackUserOpensRecommendedProductSection,
  trackUserSeesRecommendedProductSection,
} from "./recommendedProductAnalytics";
import { RecommendedProductsService } from "./recommendedProductsService";
import { useTranslation } from "react-i18next";
import { Alert } from "~/common/m3/Alert/Alert";

interface Props {
  className?: string;
}

export const PdpRecommendedProductSection = ({ className }: Props) => {
  if (!userIsCustomer()) {
    return null;
  }

  const isLoading = useSelector(selectIsLoadingExtraProduct);
  const product = useSelector(selectExtraProduct);
  const productType = useSelector(selectExtraProductType);

  const { val: isExpanded, toggle } = useBooleanState(false);

  const designation = product?.base.designation;
  useEffect(() => {
    if (designation) {
      trackUserSeesRecommendedProductSection(designation);
    }
  }, [designation]);

  useEffect(() => {
    if (designation) {
      trackUserOpensRecommendedProductSection(designation, isExpanded);
      RecommendedProductsService.addProductToRecommendedForSession(designation);
    }
  }, [isExpanded]);

  const { t } = useTranslation();

  const fakeSearchRow: SearchResultRow | null = useMemo(() => {
    if (!product) {
      return null;
    }

    return {
      productCode: product.base.code,
      features: product.base.features,
      variantProductCodes: [],
      id: -1,
    };
  }, [product]);

  if (isLoading) {
    return null;
  }

  if (!product || !fakeSearchRow) {
    return null;
  }

  return (
    <div
      className={classNames(
        "scha__extra-product-accordion",
        isExpanded && "scha__extra-product-accordion--expanded",
        className,
      )}
    >
      <Card className={"scha__extra-product-accordion__upper-card"} onClick={toggle}>
        {productType === PdpAccordionType.SUCCESSOR && (
          <Alert severity={"info"}>{t("page.pdp.extraProduct.successor")}</Alert>
        )}
        {productType === PdpAccordionType.ALTERNATIVE && (
          <Alert severity={"info"}>{t("page.pdp.extraProduct.alternate")}</Alert>
        )}
        <ToggleIcon className={"scha__extra-product-accordion__toggle"} isActive={isExpanded} />
      </Card>
      <Collapse in={isExpanded}>
        {userIsDistributor() || userIsConsumerDistributor() ? (
          <SearchResultProductCustomer
            productCode={product.base.code}
            index={0}
            onProductClick={() => trackUserClickRecommendedProductSectionDetails(product.base.designation)}
            searchQuery={""}
            isPdp={true}
          />
        ) : (
          <SearchResultProductNonCustomer
            resultRow={fakeSearchRow}
            index={0}
            onProductClick={() => trackUserClickRecommendedProductSectionDetails(product.base.designation)}
            isPdp={true}
          />
        )}
      </Collapse>
    </div>
  );
};

const selectIsLoadingExtraProduct = (root: RootState) => {
  if (root.pdp.accordionLoading) {
    return true;
  }
  if (!root.pdp.accordionProductCode) {
    return false;
  }
  const product = selectExtraProduct(root);

  return product?.isLoading ?? false;
};

const selectExtraProduct = (root: RootState) => {
  if (!root.pdp.accordionProductCode) {
    return null;
  }
  const productDescriptor = selectProductDescriptor(root, {
    productCode: root.pdp.accordionProductCode,
    priceReference: root.pdp.selectedPriceReference,
  });
  if (!productDescriptor.base) {
    return null;
  }
  return productDescriptor;
};

const selectExtraProductType = (root: RootState) => {
  return root.pdp.accordionType;
};
